import React, {useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {FieldArray} from 'formik'
import _ from 'lodash'
import {
  AdminCompanyPurchaseDTO,
  BaseIdRequest,
  BooleanApiData,
  UpsertPurchasePageDataDto,
  UpsertPurchasePageDataDtoApiData,
  UpsertPurchaseRequest,
} from '../../../../api/data-contracts'
import {
  Api_Admin_GetUpsertPurchasePageData,
  Api_Admin_RestartPurchase,
  Api_Admin_StopPurchase,
  Api_Admin_UpsertPurchase,
} from '../../../../apiClient/apiClient'
import {getRequestAsync, postRequestAsync} from '../../../../redux/commonSlice'
import FormikForm from '../../../form/FormikForm'
import CustomDropdown from '../../../form/dropdown/CustomDropdown'
import FormInput from '../../../form/FormInput'
import {
  PaymentStateEnum,
  ServicePeriodEnum,
  UserTypeAuthorizationsEnum,
} from '../../../../apiClient/enums'
import * as yup from 'yup'
import moment from 'moment'
import Translator from '../../../common/translate/Translator'
import AreYouSurePopup from '../../../common/popup/AreYouSurePopup'
import {getAdminState} from '../../../../redux/adminSlice'
import AuthorizedComponent from '../../../common/auth/AuthorizedComponent'

function UpsertPurchase(props: {
  purchase?: AdminCompanyPurchaseDTO | null
  load?: () => void
  close: () => void
}) {
  const dispatch = useDispatch()
  const [pageData, setPageData] = useState<UpsertPurchasePageDataDto | null>(null)
  const isNewPurchase = !((props.purchase?.purchaseId ?? 0) > 0)

  useEffect(() => {
    dispatch<any>(
      getRequestAsync({
        getRequestFunction: Api_Admin_GetUpsertPurchasePageData,
      })
    )
      .unwrap()
      .then(async (response: UpsertPurchasePageDataDtoApiData) => {
        setPageData(response.data ?? null)
      })
      .catch(() => {
        // Handle error if needed
      })
  }, [dispatch])

  const adminState = useSelector(getAdminState)

  const initData: UpsertPurchaseRequest = useMemo(
    () => ({
      purchaseId: props.purchase?.purchaseId ?? 0,
      productId: props.purchase?.product?.productId ?? 0,
      startTime: props.purchase?.firstPurchaseTime
        ? moment(props.purchase.firstPurchaseTime).format('YYYY-MM-DDTHH:mm')
        : moment().format('YYYY-MM-DDTHH:mm'),
      finishTime: props.purchase?.finishTime
        ? moment(props.purchase.finishTime).format('YYYY-MM-DDTHH:mm')
        : null,
      companyId: adminState.company?.company?.guid ?? '',
      payments:
        props.purchase?.payments?.map((payment) => ({
          paymentId: payment.paymentId ?? null,
          dueDate: payment.dueDate ? moment(payment.dueDate).format('YYYY-MM-DD') : '',
          state: payment.paymentState ?? null,
          paymentShares: payment.serviceProviders,
        })) ?? [],
    }),
    [props.purchase, adminState.company?.company?.guid, pageData]
  )

  const submit = async (values: UpsertPurchaseRequest) => {
    dispatch<any>(
      postRequestAsync({
        postRequestFunction: Api_Admin_UpsertPurchase,
        body: values,
      })
    )
      .unwrap()
      .then(async (response: BooleanApiData) => {
        props.load?.()
        //props.close?.()
      })
  }

  const schema = yup.object().shape({
    productId: yup.number().required('URUN_SECINIZ'),
  })

  function getPaymentStateBackgroundColor(state: number | null | undefined): string {
    if (!state) return ''

    if (state === PaymentStateEnum.Abonelik_Ödeme_Başarılı || state === PaymentStateEnum.Başarılı) {
      return 'bg-light-success'
    }

    if (
      state === PaymentStateEnum.Abonelik_Ödeme_Başarısız ||
      state === PaymentStateEnum.Başarısız
    ) {
      return 'bg-light-danger'
    }

    if (state === PaymentStateEnum.Approve_Başarısız || state === PaymentStateEnum.İade_Edildi) {
      return 'bg-light-info'
    }

    if (
      state === PaymentStateEnum.Abonelik_Ödeme_İşleniyor ||
      state === PaymentStateEnum.Bekleyen_Abonelik_Ödemesi ||
      state === PaymentStateEnum.Ödeme_Devam_Ediyor ||
      state === PaymentStateEnum.Ödeme_Oluşturuluyor
    ) {
      return 'bg-light-warning'
    }

    return ''
  }

  function createNewPayment(dueDate?: string) {
    // Get paymentShares from the latest payment (first after sorting by dueDate desc)
    const payments = props.purchase?.payments || []
    let latestPaymentShares = null

    if (payments.length > 0) {
      // Find the latest payment by dueDate
      const sortedPayments = _.orderBy(
        payments,
        [
          (payment) => {
            return payment.dueDate ? new Date(payment.dueDate).getTime() : 0
          },
        ],
        ['desc']
      )

      const latestPayment = sortedPayments[0]
      if (latestPayment?.serviceProviders && latestPayment.serviceProviders.length > 0) {
        // Copy the structure with amount set to 0
        latestPaymentShares = latestPayment.serviceProviders.map((sp) => ({
          serviceProviderId: sp.serviceProviderId ?? 0,
          amount: 0,
        }))
      }
    } else {
      const companyServiceProviderId = adminState.company?.assignedFinancialAdvisorUserId ?? 0
      latestPaymentShares = [
        {
          serviceProviderId:
            pageData?.serviceProviders?.find((sp) => sp.userId === companyServiceProviderId)?.id ??
            0,
          amount: 0,
        },
        {
          serviceProviderId: 6,
          amount: 0,
        },
      ]
    }

    return {
      paymentId: null,
      dueDate: dueDate ?? '',
      state: PaymentStateEnum.Bekleyen_Abonelik_Ödemesi,
      paymentShares: latestPaymentShares,
    }
  }

  function stopPurchase(purchaseId: number) {
    const body: BaseIdRequest = {
      id: purchaseId,
    }

    dispatch<any>(
      postRequestAsync({
        postRequestFunction: Api_Admin_StopPurchase,
        body: body,
      })
    )
      .unwrap()
      .then(async function (response: BooleanApiData) {
        if (response.data) {
          props.load?.()
        }
      })
  }

  function startPurchase(purchaseId: number) {
    const body: BaseIdRequest = {
      id: purchaseId,
    }
    dispatch<any>(
      postRequestAsync({
        postRequestFunction: Api_Admin_RestartPurchase,
        body: body,
      })
    )
      .unwrap()
      .then(async function (response: BooleanApiData) {
        if (response.data) {
          props.load && props.load()
        }
      })
  }

  return (
    <FormikForm
      initialValues={initData}
      onSubmit={submit}
      enableReinitialize={true}
      validationSchema={schema}
    >
      {(formikProps) => {
        const getPaymentTotal = (paymentShares: any[] | null | undefined): number => {
          return (
            paymentShares?.reduce(
              (sum, share) => sum + (share.amount ?? 0) + (share.discount ?? 0),
              0
            ) ?? 0
          )
        }

        return (
          <>
            <style>{`
              input[type="number"]::-webkit-inner-spin-button,
              input[type="number"]::-webkit-outer-spin-button {
                -webkit-appearance: none;
                margin: 0;
              }
              input[type="number"] {
                -moz-appearance: textfield;
              }              
            `}</style>
            {/* <div>{JSON.stringify(formikProps.values)}</div> */}
            <div className='row'>
              <div className='d-flex flex-column mb-7 col-xl-4 col-12'>
                <CustomDropdown
                  fieldAttributes={{
                    name: 'productId',
                    placeholder: 'Ürün',
                  }}
                  noSelectionText='URUN_SECINIZ'
                  options={_.orderBy(
                    pageData?.products?.map((product) => ({
                      label: `${product.productTypeName ?? ''} - ${product.title ?? ''}`,
                      value: product.id ?? 0,
                    })) ?? [],
                    ['label'],
                    ['asc']
                  )}
                  noSelectionValue={0}
                  placeHolderOnTop={true}
                  setFieldValue={(field: string, value: any) => {
                    formikProps.setFieldValue(field, value)

                    // Generate payments when product is selected for new purchase
                    if (isNewPurchase && value > 0) {
                      formikProps.setFieldValue('payments', [])
                      setTimeout(() => {
                        const productId = value
                        const startDate =
                          formikProps.values.startTime || moment().format('YYYY-MM-DDTHH:mm')
                        const product = pageData?.products?.find((p) => p.id === productId)
                        if (!product) return

                        const serviceType = product.serviceType
                        const payments: any[] = []

                        if (
                          serviceType === ServicePeriodEnum.Yıllık ||
                          serviceType === ServicePeriodEnum.Tek_Sefer
                        ) {
                          // Add one payment with startDate as dueDate
                          const payment = createNewPayment(moment(startDate).format('YYYY-MM-DD'))
                          payments.push(payment)
                        } else if (serviceType === ServicePeriodEnum.Aylık) {
                          // Add payments until end of year, one per month
                          const start = moment(startDate)
                          const endOfYear = moment(startDate).endOf('year')
                          let currentDate = moment(start)

                          while (currentDate.isSameOrBefore(endOfYear)) {
                            const payment = createNewPayment(currentDate.format('YYYY-MM-DD'))
                            payments.push(payment)
                            currentDate = moment(currentDate).add(1, 'month')
                          }
                        }

                        // Set payments
                        formikProps.setFieldValue('payments', payments)
                      }, 0)
                    }
                  }}
                  valueConverter={parseInt}
                  disabled={!isNewPurchase}
                />
              </div>
              <div className='d-flex flex-column mb-7 col-xl-2 col-12'>
                <FormInput
                  fieldAttributes={{
                    type: 'datetime-local',
                    name: 'startTime',
                    placeholder: 'Başlangıç Tarihi',
                    disabled: true,
                  }}
                  istouched={formikProps.touched.startTime ?? false}
                  errorMessage={formikProps.errors.startTime}
                  placeHolderOnTop={true}
                  checkValid={false}
                />
              </div>
              <div className='d-flex flex-column mb-7 col-xl-2 col-12'>
                <FormInput
                  fieldAttributes={{
                    type: 'datetime-local',
                    name: 'finishTime',
                    placeholder: 'Bitiş Tarihi',
                    disabled: true,
                  }}
                  istouched={formikProps.touched.finishTime ?? false}
                  errorMessage={formikProps.errors.finishTime}
                  placeHolderOnTop={true}
                  checkValid={false}
                />
              </div>
            </div>
            <div className='row'>
              <div className='d-flex flex-column mb-7 col-xl-12'>
                <FieldArray
                  name='payments'
                  render={(arrayHelpers) => (
                    <div className='d-flex flex-column justify-content-center'>
                      <div className='card card-dashed ps-6 pe-6 pt-6 mb-3'>
                        <div className='row align-items-end'>
                          <div className='d-flex flex-row mb-3 col-xl-12 col-12 align-items-center justify-content-end gap-2'>
                            {(props.purchase?.canEditAmount || isNewPurchase) && (
                              <button
                                type='button'
                                className='btn btn-success'
                                onClick={() => {
                                  const newPayment = createNewPayment()
                                  arrayHelpers.push(newPayment)
                                }}
                              >
                                <Translator name='Yeni Ödeme Ekle' />
                              </button>
                            )}

                            {props.purchase?.isStoppable && (
                              <AreYouSurePopup
                                modalOpener={
                                  <div className='btn btn-danger'>
                                    <Translator name='SATIN_ALINAN_URUNU_DURDUR' />
                                  </div>
                                }
                                message={'SATIN_ALINAN_URUNU_DURDURMEK_ISTEDIGINIZDEN_EMIN_MISINIZ'}
                                onClick={() => stopPurchase(props.purchase?.purchaseId ?? 0)}
                              />
                            )}

                            {props.purchase?.isStartable && (
                              <div className='col-2 d-flex align-items-center'>
                                <div className='d-flex justify-content-center align-items-start'>
                                  <AreYouSurePopup
                                    modalOpener={
                                      <div className='btn btn-sm btn-success'>
                                        <Translator name='SATIN_ALINAN_URUNU_DEVAM_ETTIR' />
                                      </div>
                                    }
                                    message={
                                      'SATIN_ALINAN_URUNU_DEVAM_ETTIRMEK_ISTEDIGINIZDEN_EMIN_MISINIZ'
                                    }
                                    onClick={() => startPurchase(props.purchase?.purchaseId ?? 0)}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {formikProps.values.payments && formikProps.values.payments.length > 0 && (
                        <div className='card'>
                          <div className='card-body'>
                            <table
                              className='table table-row-bordered table-row-dashed align-middle gy-3'
                              style={{width: '100%'}}
                            >
                              <thead>
                                <tr className='fw-bold text-gray-700 fs-6 text-uppercase'>
                                  <th style={{width: '20%'}} className='align-top text-center'>
                                    Ödeme Durumu
                                  </th>
                                  <th style={{width: '10%'}} className='align-top text-center'>
                                    Vade Tarihi
                                  </th>
                                  <th style={{width: '10%'}} className='align-top text-center'>
                                    Ödeme Tarihi
                                  </th>
                                  <th style={{width: '30%'}} className='align-top text-center'>
                                    Ödeme Detayları
                                  </th>
                                  <th style={{width: '7%'}} className='align-top text-center'>
                                    Toplam
                                  </th>
                                  <th style={{width: '23%'}} className='align-top text-center'>
                                    İşlemler
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {formikProps.values.payments
                                  .map((payment, index) => ({
                                    payment,
                                    index,
                                    originalPayment: props.purchase?.payments?.find(
                                      (p) => p.paymentId === payment.paymentId
                                    ),
                                  }))
                                  .sort((a, b) => {
                                    const dateA = a.payment.dueDate
                                      ? new Date(a.payment.dueDate).getTime()
                                      : 0
                                    const dateB = b.payment.dueDate
                                      ? new Date(b.payment.dueDate).getTime()
                                      : 0
                                    return dateB - dateA // Descending order
                                  })
                                  .map(({payment, index, originalPayment}) => {
                                    return (
                                      <tr
                                        key={index}
                                        style={{
                                          minHeight:
                                            originalPayment?.discountAmount != null &&
                                            originalPayment.discountAmount < 0
                                              ? 'auto'
                                              : 'auto',
                                        }}
                                      >
                                        <td className='align-top'>
                                          <CustomDropdown
                                            fieldAttributes={{
                                              name: `payments.${index}.state`,
                                              placeholder: 'Ödeme Durumu',
                                              disabled: originalPayment?.isSuccessful ?? false,
                                            }}
                                            noSelectionText='ODEME_DURUMU_SECINIZ'
                                            options={
                                              pageData?.paymentStates?.map((state) => ({
                                                label: state.description ?? '',
                                                value: state.id ?? 0,
                                              })) ?? []
                                            }
                                            noSelectionValue={0}
                                            placeHolderOnTop={false}
                                            setFieldValue={formikProps.setFieldValue}
                                            valueConverter={parseInt}
                                            className={getPaymentStateBackgroundColor(
                                              payment.state
                                            )}
                                          />
                                          <AuthorizedComponent
                                            auth={
                                              UserTypeAuthorizationsEnum.Admin_Panel_Company_Payment_Error
                                            }
                                          >
                                            {originalPayment?.errorInfo && (
                                              <div className='text-danger mt-2 ms-2'>
                                                {originalPayment.errorInfo}
                                              </div>
                                            )}
                                          </AuthorizedComponent>
                                        </td>
                                        <td className='align-top'>
                                          <FormInput
                                            fieldAttributes={{
                                              type: 'date',
                                              name: `payments.${index}.dueDate`,
                                              placeholder: 'Vade Tarihi',
                                              disabled: originalPayment?.isSuccessful ?? false,
                                            }}
                                            istouched={
                                              (formikProps.touched.payments as any)?.[index]
                                                ?.dueDate ?? false
                                            }
                                            errorMessage={
                                              (formikProps.errors.payments as any)?.[index]?.dueDate
                                            }
                                            placeHolderOnTop={false}
                                            checkValid={false}
                                          />
                                        </td>
                                        <td className='align-top'>
                                          <div className='form-control form-control-solid text-end'>
                                            {originalPayment?.paymentTime
                                              ? moment(originalPayment.paymentTime).format(
                                                  'DD/MM/YYYY'
                                                )
                                              : '-'}
                                          </div>
                                        </td>

                                        {payment.paymentShares &&
                                        payment.paymentShares.length > 0 ? (
                                          <>
                                            <td>
                                              <table className='table table-sm table-bordered mb-0'>
                                                <thead>
                                                  <tr>
                                                    <th
                                                      style={{width: '30%'}}
                                                      className='fw-bold align-middle'
                                                    >
                                                      Hizmet Sağlayıcı
                                                    </th>
                                                    <th
                                                      style={{width: '25%'}}
                                                      className='text-end fw-bold'
                                                    >
                                                      Tutar
                                                    </th>
                                                    <th
                                                      style={{width: '25%'}}
                                                      className='text-end fw-bold'
                                                    >
                                                      İndirim
                                                    </th>
                                                    <th
                                                      style={{width: '20%'}}
                                                      className='text-end fw-bold'
                                                    >
                                                      Ara Toplam
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {payment.paymentShares.map(
                                                    (share, shareIndex) => {
                                                      return (
                                                        <tr key={shareIndex}>
                                                          <td className='align-middle'>
                                                            {share.serviceProviderName}
                                                          </td>
                                                          <td className='text-end'>
                                                            <FormInput
                                                              fieldAttributes={{
                                                                type: 'number',
                                                                name: `payments.${index}.paymentShares.${shareIndex}.amount`,
                                                                placeholder: '0.00',
                                                                step: '0.01',
                                                                style: {
                                                                  textAlign: 'right',
                                                                  maxWidth: '120px',
                                                                },
                                                                disabled:
                                                                  originalPayment?.isSuccessful ??
                                                                  false,
                                                              }}
                                                              istouched={
                                                                (
                                                                  formikProps.touched
                                                                    .payments as any
                                                                )?.[index]?.paymentShares?.[
                                                                  shareIndex
                                                                ]?.amount ?? false
                                                              }
                                                              errorMessage={
                                                                (
                                                                  formikProps.errors.payments as any
                                                                )?.[index]?.paymentShares?.[
                                                                  shareIndex
                                                                ]?.amount
                                                              }
                                                              placeHolderOnTop={false}
                                                              checkValid={false}
                                                            />
                                                          </td>
                                                          <td className='text-end'>
                                                            <FormInput
                                                              fieldAttributes={{
                                                                type: 'number',
                                                                name: `payments.${index}.paymentShares.${shareIndex}.discount`,
                                                                placeholder: '0.00',
                                                                step: '0.01',
                                                                style: {
                                                                  textAlign: 'right',
                                                                  maxWidth: '120px',
                                                                },
                                                                disabled:
                                                                  originalPayment?.isSuccessful ??
                                                                  false,
                                                              }}
                                                              istouched={
                                                                (
                                                                  formikProps.touched
                                                                    .payments as any
                                                                )?.[index]?.paymentShares?.[
                                                                  shareIndex
                                                                ]?.discount ?? false
                                                              }
                                                              errorMessage={
                                                                (
                                                                  formikProps.errors.payments as any
                                                                )?.[index]?.paymentShares?.[
                                                                  shareIndex
                                                                ]?.discount
                                                              }
                                                              placeHolderOnTop={false}
                                                              checkValid={false}
                                                            />
                                                          </td>
                                                          <td className='text-end'>
                                                            <span className='form-control form-control-solid d-inline-block text-end '>
                                                              {Math.round(
                                                                (share.amount ?? 0) +
                                                                  (share.discount ?? 0)
                                                              ).toString()}
                                                            </span>
                                                          </td>
                                                        </tr>
                                                      )
                                                    }
                                                  )}
                                                </tbody>
                                              </table>
                                            </td>
                                          </>
                                        ) : (
                                          <td></td>
                                        )}
                                        <td className='align-bottom'>
                                          <span className='form-control form-control-solid d-inline-block text-end mb-3'>
                                            {Math.round(
                                              getPaymentTotal(payment.paymentShares)
                                            ).toString()}
                                          </span>
                                        </td>
                                        <td className='align-top'>
                                          {payment.paymentId === null && (
                                            <button
                                              type='button'
                                              className='btn btn-sm btn-icon btn-light-danger'
                                              onClick={() => arrayHelpers.remove(index)}
                                              title='Kaldır'
                                            >
                                              <i className='fa-solid fa-x'></i>
                                            </button>
                                          )}
                                          <div
                                            className={
                                              originalPayment?.isInvoicePrinted
                                                ? 'text-success'
                                                : 'text-danger'
                                            }
                                          >
                                            {!originalPayment?.invoiceUrl && (
                                              <div className='row'>
                                                <div className='text-center'>
                                                  {originalPayment?.isInvoicePrinted
                                                    ? 'Basıldı'
                                                    : 'Basılmadı'}
                                                </div>
                                              </div>
                                            )}
                                            <AuthorizedComponent
                                              auth={
                                                UserTypeAuthorizationsEnum.Admin_Panel_Company_Invoice_Download
                                              }
                                            >
                                              {originalPayment?.invoiceUrl && (
                                                <div className='row'>
                                                  <div className='d-flex justify-content-center align-items-start'>
                                                    <a
                                                      className='btn btn-sm btn-primary'
                                                      href={originalPayment?.invoiceUrl ?? ''}
                                                      target='_blank'
                                                      rel='noreferrer'
                                                    >
                                                      <span className='indicator-label'>
                                                        <Translator name='FATURA_PDF_INDIR' />
                                                      </span>
                                                    </a>
                                                  </div>
                                                </div>
                                              )}
                                            </AuthorizedComponent>
                                          </div>
                                        </td>
                                      </tr>
                                    )
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                />
                <div className='d-flex justify-content-end mt-5'>
                  {(props.purchase?.canEditAmount || isNewPurchase) && (
                    <button type='submit' className='btn btn-primary'>
                      <Translator name='KAYDET' />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )
      }}
    </FormikForm>
  )
}

export default UpsertPurchase
